import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { truthy } from '@nestech/utils';
import {
  AuthenticatedMainRoute,
  BaseGuards,
  ExtendedRoute,
  MainComponent,
  SideNavigationRouteMenuItemState,
  SideNavigationService,
} from '@web/base';
import { NotAllowed } from '@web/common';
import {
  Authentication,
  CampaignApi,
  DocumentStore,
  Environment,
  MemberStore,
  OnboardingService,
  provideNotification,
  SsoTransferService,
} from '@web/service';
import { firstValueFrom } from 'rxjs';
import { authenticationMatchFn } from './authentication-guard';
import { AuthenticatedRoot } from './component';
import { SignDocumentAction } from './sign-document-action';

const logoutRedirect: CanActivateFn = async () => {
  const authService = inject(Authentication);
  authService.logout();
  return false;
};

const openPensionsInfo: CanActivateFn = async () => {
  const ssoTransferService = inject(SsoTransferService);

  await ssoTransferService.openPensionInfoSSOPopup();
  return false;
};

const openVicorda: CanActivateFn = async () => {
  const ssoTransferService = inject(SsoTransferService);

  await ssoTransferService.openVicordaSSOPopup();
  return false;
};

const signDocument: CanActivateFn = async activatedRoute => {
  const signService = inject(SignDocumentAction);
  await signService.signDocumentByType(activatedRoute.params['type']);
  return false;
};

type AuthenticatedRootType = {
  authenticatedRoot: ExtendedRoute;
};

export const buildAuthenticatedRoute = (environment: Environment): AuthenticatedRootType => ({
  authenticatedRoot: {
    path: '',
    component: AuthenticatedRoot,
    canMatch: [authenticationMatchFn, BaseGuards.memberLoaded],
    providers: [
      provideNotification(environment),
      DocumentStore,
      CampaignApi,
      OnboardingService,
      SideNavigationService,
      SideNavigationRouteMenuItemState,
    ],
    children: [
      {
        path: '',
        canMatch: [BaseGuards.memberBlocked],
        children: [
          { path: 'not-allowed', component: NotAllowed },
          { path: '**', redirectTo: '/not-allowed' },
        ],
      },
    ],
  },
});

export const AuthenticatedMainComponentRoute: { mainComponentRoot: ExtendedRoute } = {
  mainComponentRoot: {
    path: '',
    component: MainComponent,
  },
};

export const authenticatedMainComponentChildren = (
  environment: Environment,
): Record<AuthenticatedMainRoute, ExtendedRoute> =>
  ({
    root: {
      path: '',
      pathMatch: 'full',
      redirectTo: async () => {
        const router = inject(Router);
        const service = inject(SideNavigationService);
        const memberStore = inject(MemberStore);
        await firstValueFrom(memberStore.member$.pipe(truthy()));
        const route = service.menuItems()?.[0]?.routerLink ?? ['home'];
        return router.createUrlTree(route);
      },
    },

    // Main feature routes
    home: {
      path: 'home',
      canMatch: [BaseGuards.areaNotHidden],
      menuItem: {
        label: 'cas.overview',
        icon: 'objects-column',
      },
    },
    savings: {
      path: 'savings',
      canMatch: [BaseGuards.areaNotHidden],
      loadChildren: () => import('@web/savings').then(m => m.buildSavingsRoutes(environment)),
      menuItem: {
        label: 'cas.savings',
        icon: 'coins',
      },
    },

    risks: {
      path: 'risks',
      canMatch: [BaseGuards.areaNotHidden],
      loadChildren: () => import('@web/risk/lazy').then(m => m.buildRiskRoutes(environment)),
      menuItem: {
        icon: 'shield-check',
        label: 'cas.insurance',
      },
    },
    payout: {
      path: 'payout',
      canMatch: [BaseGuards.areaNotHidden],
      loadChildren: () => import('@web/payout/lazy').then(m => m.buildPayoutRoutes(environment)),
      menuItem: {
        icon: 'sack',
        label: 'cas.menuDisbursement',
        dividerFn: ({ hasDisbursements, policyState }) =>
          hasDisbursements || policyState['hasAnyCoverCollection'] ? ['after'] : [],
      },
    },
    document: {
      path: 'document',
      canMatch: [BaseGuards.areaNotHidden],
      loadChildren: () =>
        import('@web/document').then(m => m.buildDocumentRoutes(environment, 'outbound', 'inbound', 'conditions')),
      menuItem: {
        icon: 'file-signature',
        label: 'cas.documents',
        notificationId: 'UNREAD_DOCUMENTS',
        queryParams: { sort: 'createdDate:desc' },
      },
    },
    selfService: {
      path: 'self-service',
      canMatch: [BaseGuards.areaNotHidden],
      menuItem: {
        icon: 'pen-field',
        label: 'cas.selfService',
      },
    },

    contact: {
      path: 'contact',
      canMatch: [BaseGuards.areaNotHidden],
      loadChildren: () => import('@web/contact').then(m => m.buildContactRoutes(environment)),
      menuItem: {
        icon: 'circle-question',
        label: 'cas.contact',
        notificationId: 'NEW_CHAT_MESSAGE',
        dividerFn: () => ['before', 'after'],
      },
    },

    profile: {
      path: 'profile',
      loadChildren: () => import('@web/profile').then(m => m.buildProfileRoutes(environment)),
    },
    logout: {
      path: 'logout',
      canActivate: [logoutRedirect],
      component: {} as any,
      menuItem: {
        icon: 'right-from-bracket',
        label: 'cas.logout',
        priority: Number.MAX_SAFE_INTEGER,
      },
    },
    pensionsInfo: {
      path: 'integration/pensionsinfo',
      component: {} as any,
      canActivate: [openPensionsInfo],
      children: [],
    },
  }) as const;

export const openVicordaRoute = (environment: Environment): ExtendedRoute => ({
  path: 'integration/vicorda',
  canActivate: [openVicorda],
  children: [],
});

export const signDocumentRoute = (environment: Environment): ExtendedRoute => ({
  path: 'wizard/underskriv/:type',
  canActivate: [signDocument],
  children: [],
});

export const RestrictedAccessRoute: ExtendedRoute = {
  path: '',
  canMatch: [BaseGuards.memberBlocked],
};
